// src/components/pdf-table/BulkDocumentSetSelector.js
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Plus } from 'lucide-react';
import DocumentSetPill from '../document-sets/DocumentSetPill';
import SelectionCheckbox from './SelectionCheckbox';
import SetListItem from '../document-sets/SetListItem';
import SearchInput from '../ui/SearchInput';
import { getRandomColor } from '../../utils/documentSetColors';
import ScrollContainer from '../ui/ScrollContainer';
import { trackBulkSetSelectorOpened, trackBulkSetSelectorSubmitted } from '../../utils/analytics';

/**
 * Bulk Document Set Selector Component
 * Used for bulk add/remove operations with batched changes and submit button
 * 
 * @param {string} mode - 'add' or 'remove'
 * @param {Array} availableSets - All sets available for selection
 * @param {Array} initialSelectedIds - Initially selected set IDs
 * @param {number} selectedCount - Number of documents selected
 * @param {Function} onSubmit - Called with array of set IDs to add/remove
 * @param {Function} onCreateSet - Called when creating new set
 */
const BulkDocumentSetSelector = ({
  mode = 'add',
  availableSets = [],
  initialSelectedIds = [],
  selectedCount = 0,
  onSubmit,
  onCreateSet,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingSelectedIds, setPendingSelectedIds] = useState(
    mode === 'remove' ? [] : initialSelectedIds
  );
  const searchInputRef = useRef(null);

  // Filter available sets based on search
  const filteredSets = useMemo(() => {
    if (!searchQuery.trim()) return availableSets;
    
    const query = searchQuery.toLowerCase();
    return availableSets.filter(set => 
      set.name.toLowerCase().includes(query)
    );
  }, [availableSets, searchQuery]);

  // Check if search query could create a new set (only in add mode)
  const canCreateNew = useMemo(() => {
    if (mode !== 'add' || !searchQuery.trim()) return false;
    
    const exactMatch = availableSets.some(
      set => set.name.toLowerCase() === searchQuery.trim().toLowerCase()
    );
    
    return !exactMatch;
  }, [mode, availableSets, searchQuery]);

  // Preview color for new set
  const [previewColor, setPreviewColor] = useState(null);

  useEffect(() => {
    if (searchQuery.trim() && !previewColor) {
      setPreviewColor(getRandomColor(availableSets));
    } else if (!searchQuery.trim() && previewColor) {
      setPreviewColor(null);
    }
  }, [searchQuery, previewColor, availableSets]);

  // Focus search input on mount and track opening
  useEffect(() => {
    if (searchInputRef.current) {
      // SearchInput wraps an input, so we need to find it
      const input = searchInputRef.current.querySelector('input');
      if (input) input.focus();
    }

    // Track that the bulk set selector was opened
    trackBulkSetSelectorOpened(mode, availableSets.length);
  }, [mode, availableSets.length]);

  // Handle creating a new set
  const handleCreateSet = useCallback(async () => {
    if (!canCreateNew || !searchQuery.trim() || !previewColor) return;
    
    const newSetName = searchQuery.trim();
    const newSet = await onCreateSet?.(newSetName, previewColor);
    
    if (newSet?.id) {
      setPendingSelectedIds(prev => [...prev, newSet.id]);
    }
    
    setSearchQuery('');
  }, [canCreateNew, searchQuery, previewColor, onCreateSet]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && canCreateNew) {
      e.preventDefault();
      handleCreateSet();
    }
  }, [canCreateNew, handleCreateSet]);

  // Handle set toggle
  const handleSetToggle = useCallback((set) => {
    if (set.archived) return;

    setPendingSelectedIds(prev => {
      const isSelected = prev.includes(set.id);
      if (isSelected) {
        return prev.filter(id => id !== set.id);
      } else {
        return [...prev, set.id];
      }
    });
  }, []);

  // Handle select/deselect all
  const handleToggleAll = useCallback(() => {
    const unarchived = filteredSets.filter(s => !s.archived);
    const unarchivedIds = unarchived.map(s => s.id);
    const selectedUnarchivedCount = unarchivedIds.filter(id => pendingSelectedIds.includes(id)).length;

    if (selectedUnarchivedCount === unarchived.length) {
      // All unarchived are selected - deselect all
      setPendingSelectedIds(prev => prev.filter(id => !unarchivedIds.includes(id)));
    } else {
      // Some or none selected - select all unarchived
      setPendingSelectedIds(prev => {
        const newIds = [...prev];
        unarchivedIds.forEach(id => {
          if (!newIds.includes(id)) {
            newIds.push(id);
          }
        });
        return newIds;
      });
    }
  }, [filteredSets, pendingSelectedIds]);

  // Calculate header checkbox state
  const headerCheckboxState = useMemo(() => {
    const unarchived = filteredSets.filter(s => !s.archived);
    if (unarchived.length === 0) return { checked: false, indeterminate: false };

    const selectedUnarchivedCount = unarchived.filter(s => pendingSelectedIds.includes(s.id)).length;
    const isAllSelected = selectedUnarchivedCount === unarchived.length;
    const isSomeSelected = selectedUnarchivedCount > 0 && !isAllSelected;

    return { checked: isAllSelected, indeterminate: isSomeSelected };
  }, [filteredSets, pendingSelectedIds]);

  // Calculate changes to apply
  const changesToApply = useMemo(() => {
    if (mode === 'add') {
      // Changes are sets in pending but not in initial
      return pendingSelectedIds.filter(id => !initialSelectedIds.includes(id));
    } else {
      // In remove mode, changes are sets selected for removal
      return pendingSelectedIds;
    }
  }, [mode, pendingSelectedIds, initialSelectedIds]);

  // Can submit if there are changes
  const canSubmit = mode === 'remove' 
    ? changesToApply.length > 0
    : changesToApply.length > 0;

  const handleSubmit = () => {
    // Track submission with mode and number of sets selected
    trackBulkSetSelectorSubmitted(mode, changesToApply.length);
    onSubmit?.(changesToApply);
  };

  const buttonText = mode === 'add'
    ? `Add to ${selectedCount} docs`
    : `Remove from ${selectedCount} docs`;

  return (
    <div className="p-app-lg w-[288px] h-[256px] flex flex-col">
      {/* Search input - using SearchInput component */}
      <div ref={searchInputRef} className="mb-app-lg">
        <SearchInput
          searchTerm={searchQuery}
          onSearchChange={setSearchQuery}
          placeholder="Search or create..."
          dark
        />
      </div>

      {/* Set list with checkbox multiselect */}
      <ScrollContainer className="flex-1 min-h-0 mb-2">
        {filteredSets.length === 0 && !canCreateNew ? (
          <div className="px-app-xl py-app-lg text-sm text-app-gray-400 text-center">
            {mode === 'add' ? 'No tags (yet). Enter a name to create one!' : 'No tags to remove'}
          </div>
        ) : (
          <>
            {/* Header with select all checkbox */}
            {filteredSets.length > 0 && (
              <div className="flex items-center gap-2 px-2 py-1 mb-1">
                <SelectionCheckbox
                  checked={headerCheckboxState.checked}
                  indeterminate={headerCheckboxState.indeterminate}
                  onChange={handleToggleAll}
                  variant={mode === 'remove' ? 'x' : 'check'}
                  darkMode
                />
                <span className="text-app-xs text-app-gray-400 font-medium">
                  Select All
                </span>
              </div>
            )}

            {/* Set list items */}
            {filteredSets.map(set => {
              const isSelected = pendingSelectedIds.includes(set.id);
              return (
                <SetListItem
                  key={set.id}
                  set={set}
                  isSelected={isSelected}
                  isArchived={set.archived}
                  onToggle={() => handleSetToggle(set)}
                  mode={mode === 'remove' ? 'x' : 'check'}
                  darkMode
                />
              );
            })}

            {/* Create new set option (only in add mode) */}
            {canCreateNew && previewColor && (
              <>
                <div className="border-t border-app-gray-600 my-2" />
                <button
                  onClick={handleCreateSet}
                  className="w-full flex items-center gap-2 px-2 py-1 hover:bg-app-gray-700 rounded transition-colors"
                >
                  <Plus className="w-4 h-4 text-app-accent flex-shrink-0" />
                  <DocumentSetPill
                    documentSet={{
                      id: 'preview',
                      name: searchQuery,
                      color: previewColor
                    }}
                    onRemove={() => {}}
                    showRemove={false}
                    isDraggable={false}
                    isReadOnly={true}
                  />
                </button>
              </>
            )}
          </>
        )}
      </ScrollContainer>

      {/* Submit button */}
      <button
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="w-full btn-secondary-dark"
      >
        {buttonText}
      </button>
    </div>
  );
};

export default BulkDocumentSetSelector;